import Chip from 'tio-ui/components/chip';
import { dropTask } from 'ember-concurrency';
import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import { divide } from 'tio-ui/math';
import EmployeeEligibility from 'tio-common/components/tas/employee-eligibility';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import Table from 'tio-ui/components/table';
import { PhCreditCard } from 'ember-phosphor-icons';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import { executeSearchRequest } from 'tio-data/requests/search';
import {
  holdTasParticipantRequest,
  activateTasParticipantRequest,
} from 'tio-data/requests/tas-participant';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type IntlService from 'ember-intl/services/intl';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router';
import type { OpenSearchResponse } from 'tio-data/data/resources/search.ext';
import { reloadRecord } from 'tio-data/requests/save-record';
import type { Store as StoreService } from '@warp-drive/core';
import type { TasEligibilityExtension as TasEligibilityModel } from 'tio-data/data/resources/tas-eligibility.ext';

interface TasSearchDoc {
  id: string;
  recordType: string;

  // Instance-level fields (present on all docs)
  tasProgramInstanceIdKeyword: string;
  tasProgramInstanceStateKeyword?: string;
  tasProgramTemplateFieldProgramNameKeyword?: string;

  // Application-level fields (only on record_type: 'application')
  tasApplicationIdKeyword?: string;
  tasApplicationStateKeyword?: string;
  tasApplicationFieldApplicationNameKeyword?: string;
  tasApplicationPaymentFinalApprovedTotalAmountInteger?: number;
  tasApplicationPaymentPaidAmountInteger?: number;
  tasApplicationPaymentLastDateDate?: string;
  tasApplicationPaymentRefundRequestedAmountInteger?: number;
  tasApplicationCourseCashReservedAmountInteger?: number;
  tasApplicationCourseCashUsedAmountInteger?: number;
}

interface GroupedInstance {
  instanceId: string;
  instanceState: string;
  programName: string;
  applications: TasSearchDoc[];
}

export interface TuitionAssistanceProfileTuitionAssistanceSignature {
  Args: {
    employee: EmployeeModel;
  };
  Element: HTMLElement;
}

export default class TuitionAssistanceProfileTuitionAssistanceComponent extends Component<TuitionAssistanceProfileTuitionAssistanceSignature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare store: StoreService;

  @tracked holdParticipantModal = false;
  @tracked isCollapsed = true;

  constructor(owner: Owner, args: TuitionAssistanceProfileTuitionAssistanceSignature['Args']) {
    super(owner, args);

    // Check if we should auto-expand and scroll to eligibility
    const queryParams = this.router.currentRoute?.queryParams;
    if (queryParams?.scrollToEligibility === 'true') {
      this.isCollapsed = false;

      // Scroll to participation status after section expands
      setTimeout(() => {
        const element = document.getElementById('participation-status');
        element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }

  get employee() {
    return this.args.employee;
  }

  statusText = (state?: string): string => {
    if (!state) return '';
    if (this.intl.exists(`common.status_message.to.${state}`)) {
      return this.intl.t(`common.status_message.to.${state}`);
    }

    return this.intl.t('common.status_message.to.TAS.ProgramInstanceState.UPDATED_ON');
  };

  isCourseCash = (app: TasSearchDoc): boolean => {
    return (
      (app.tasApplicationCourseCashReservedAmountInteger ?? 0) > 0 ||
      (app.tasApplicationCourseCashUsedAmountInteger ?? 0) > 0
    );
  };

  isRefundRequested = (app: TasSearchDoc): boolean => {
    return (app.tasApplicationPaymentRefundRequestedAmountInteger ?? 0) > 0;
  };

  applicationId = (app: TasSearchDoc) => {
    return app.tasApplicationIdKeyword ?? app.id ?? null;
  };

  isDefined = (val: unknown) => {
    return val != null;
  };

  tasSearchData = trackedFunction(this, async () => {
    if (!this.employee?.id) {
      return [];
    }

    const companyId = this.employee.company?.id;
    if (!companyId) {
      return [];
    }

    const builder = new OpenSearchQueryBuilder()
      .mustTerm('company_id', companyId)
      .mustTerm('employee_id', this.employee.id)
      .mustTerms('record_type', ['application', 'instance'])
      .setSort(['-tas_program_instance_created_at_date', '-tas_application_created_at_date'])
      .setPagination(1, 200);

    const query = {
      'model-type': 'TasProgramInstance',
      'search-body': builder.build(),
    };

    // Execute via the shared search builder; unwrap `.content`.
    const { content } = await this.store.request<OpenSearchResponse<TasSearchDoc>>(
      executeSearchRequest(query)
    );
    return OpenSearchResponseBuilder.from<TasSearchDoc>(content).getDocuments();
  });

  get isLoading() {
    return this.tasSearchData.isLoading;
  }

  get searchResults() {
    return this.tasSearchData.value ?? [];
  }

  get groupedInstances(): GroupedInstance[] {
    const docs = this.searchResults;
    const instanceMap = new Map<string, GroupedInstance>();

    for (const doc of docs) {
      const instanceId = doc.tasProgramInstanceIdKeyword;
      if (!instanceId) continue;

      if (!instanceMap.has(instanceId)) {
        instanceMap.set(instanceId, {
          instanceId,
          instanceState: doc.tasProgramInstanceStateKeyword ?? '',
          programName: doc.tasProgramTemplateFieldProgramNameKeyword ?? '',
          applications: [],
        });
      }

      if (doc.recordType === 'application') {
        instanceMap.get(instanceId)!.applications.push(doc);
      }
    }

    return [...instanceMap.values()];
  }

  get showCourseCashColumns() {
    return this.searchResults.some(this.isCourseCash);
  }

  courseCashReservedDisplay = (amount?: number): number => {
    const value = amount ?? 0;
    return value < 0 ? 0 : value;
  };

  courseCashUsedDisplay = (amount?: number): number => {
    return amount ?? 0;
  };

  get employeeEligibilities() {
    return this.args.employee.tasEligibilities as TasEligibilityModel[];
  }

  get isInActive() {
    return this.employee?.tasParticipant?.state === 'TAS.ParticipantState.INACTIVE';
  }

  get holdStatus() {
    return this.tasParticipationStatus === 'ACTIVE';
  }

  get tasParticipationStatus() {
    return this.employee?.tasParticipant?.state.split('.')[2];
  }

  toggleHoldParticipantModal = () => {
    this.holdParticipantModal = !this.holdParticipantModal;
  };

  get participantId() {
    return this.employee.tasParticipant?.id;
  }

  get holdChangeNote() {
    return `Hold/Reactivate employee: ${this.employee.fullName}`;
  }

  toggleHold = dropTask(async () => {
    // WarpDrive request builders (Phase 2.7) — replace legacy adapter methods.
    // holdTasParticipantRequest / activateTasParticipantRequest mirror the
    // same PATCH endpoints the old adapter built via buildURL + ajax.
    if (this.tasParticipationStatus === 'ACTIVE') {
      await this.store.request(
        holdTasParticipantRequest(
          this.participantId as string,
          {
            changelog: this.holdChangeNote,
          } as unknown as { note: string }
        )
      );
    } else {
      await this.store.request(
        activateTasParticipantRequest(
          this.participantId as string,
          {
            changelog: this.holdChangeNote,
          } as unknown as { note: string }
        )
      );
    }
    // hold/activate go through StateTransitionableController#transition →
    // `head 204` (renders NO resource), so the builder does NOT refresh the
    // cache — keep the reload via the helper.
    await reloadRecord(this.store, this.employee.tasParticipant);
    this.toggleHoldParticipantModal();
  });

  <template>
    <Section @collapsed={{this.isCollapsed}}>
      <:header>{{t "tuition_assistance.default"}}</:header>
      <:body>
        <VStack @collapsed={{true}}>
          <Section>
            <Table @isLoading={{this.isLoading}}>
              <:thead as |options|>
                <options.tr>
                  <options.th>{{t "common.id"}}</options.th>
                  <options.th>{{t "tuition_assistance.application_name"}}</options.th>
                  <options.th>
                    {{t "tuition_assistance.program_details.application_history.state"}}
                  </options.th>
                  <options.th>
                    {{t "tuition_assistance.program_details.status.approved_total"}}
                  </options.th>
                  <options.th>
                    {{t "tuition_assistance.program_details.status.paid_total"}}
                  </options.th>
                  <options.th>
                    {{t "tuition_assistance.program_details.status.paid_date"}}
                  </options.th>
                  {{#if this.showCourseCashColumns}}
                    <options.th>
                      {{t "tuition_assistance.program_details.status.course_cash_reserved"}}
                    </options.th>
                    <options.th>
                      {{t "tuition_assistance.program_details.status.course_cash_used"}}
                    </options.th>
                  {{/if}}
                </options.tr>
              </:thead>
              <:tbody as |options|>
                {{#each this.groupedInstances as |group|}}
                  <options.tr class="align-top">
                    <options.td class="tio-fg-info underline">
                      <LinkTo
                        @route="authenticated.admin.tuition-assistance.program-instances.show"
                        @model={{group.instanceId}}
                      >
                        {{group.instanceId}}
                      </LinkTo>
                    </options.td>
                    <options.td>{{group.programName}}</options.td>
                    <options.td>{{this.statusText group.instanceState}}</options.td>
                    <options.td>-</options.td>
                    <options.td>-</options.td>
                    <options.td>-</options.td>
                    {{#if this.showCourseCashColumns}}
                      <options.td>-</options.td>
                      <options.td>-</options.td>
                    {{/if}}
                  </options.tr>
                  {{#each group.applications as |app|}}
                    <options.tr class="tio-bg-sunken">
                      <options.td class="pl-9 pr-3 tio-fg-info">
                        {{#if (this.applicationId app)}}
                          <LinkTo
                            @route="authenticated.admin.tas.applications.approval"
                            @model={{this.applicationId app}}
                            class="underline"
                          >
                            {{this.applicationId app}}
                          </LinkTo>
                        {{else}}
                          <span>-</span>
                        {{/if}}
                        {{#if (this.isRefundRequested app)}}
                          <Chip @size="sm" @intent="danger" class="ml-2 text-nowrap">
                            {{t "tuition_assistance.application_details.in_repayment"}}
                          </Chip>
                        {{/if}}
                      </options.td>
                      <options.td>
                        {{#if (this.isCourseCash app)}}
                          <PhCreditCard @size="20" class="inline-block mr-1.5 align-text-bottom" />
                        {{/if}}
                        {{app.tasApplicationFieldApplicationNameKeyword}}
                      </options.td>
                      <options.td>{{this.statusText app.tasApplicationStateKeyword}}</options.td>
                      <options.td>
                        {{#if
                          (this.isDefined app.tasApplicationPaymentFinalApprovedTotalAmountInteger)
                        }}
                          {{safeFormatNumber
                            (divide app.tasApplicationPaymentFinalApprovedTotalAmountInteger 100)
                            style="currency"
                            currency="USD"
                          }}
                        {{else}}
                          -
                        {{/if}}
                      </options.td>
                      <options.td>
                        {{#if (this.isDefined app.tasApplicationPaymentPaidAmountInteger)}}
                          {{safeFormatNumber
                            (divide app.tasApplicationPaymentPaidAmountInteger 100)
                            style="currency"
                            currency="USD"
                          }}
                        {{else}}
                          -
                        {{/if}}
                      </options.td>
                      <options.td>
                        {{if
                          app.tasApplicationPaymentLastDateDate
                          (safeFormatDate app.tasApplicationPaymentLastDateDate)
                          "-"
                        }}
                      </options.td>
                      {{#if this.showCourseCashColumns}}
                        <options.td>
                          {{#if (this.isCourseCash app)}}
                            {{safeFormatNumber
                              (divide
                                (this.courseCashReservedDisplay
                                  app.tasApplicationCourseCashReservedAmountInteger
                                )
                                100
                              )
                              style="currency"
                              currency="USD"
                            }}
                          {{else}}
                            -
                          {{/if}}
                        </options.td>
                        <options.td>
                          {{#if (this.isCourseCash app)}}
                            {{safeFormatNumber
                              (divide
                                (this.courseCashUsedDisplay
                                  app.tasApplicationCourseCashUsedAmountInteger
                                )
                                100
                              )
                              style="currency"
                              currency="USD"
                            }}
                          {{else}}
                            -
                          {{/if}}
                        </options.td>
                      {{/if}}
                    </options.tr>
                  {{/each}}
                {{else}}
                  <options.tr>
                    <options.td colspan={{if this.showCourseCashColumns "8" "6"}}>
                      {{t "common.no_records_found"}}
                    </options.td>
                  </options.tr>
                {{/each}}
              </:tbody>
            </Table>
          </Section>
          <EmployeeEligibility @employee={{@employee}} />
        </VStack>
      </:body>
    </Section>
  </template>
}
